/**
 * MapRenderer.ts
 * 2.5D isometric tile rendering. Each tile is drawn as a block: a diamond top
 * face plus shaded south-west / south-east cliff faces dropping to the ground
 * plane. Tiles are drawn back-to-front (row-major) so nearer tiles correctly
 * occlude the faces of those behind — that painter's order is what sells the
 * Warcraft-II-style depth (cliffs, a sunken pond, raised buildings).
 *
 * Terrain is split into CHUNK_SIZE×CHUNK_SIZE Graphics objects so that:
 *   1. Pixi can auto-cull off-screen chunks (cullable=true, zero CPU per chunk).
 *   2. Boot cost is spread across frames — visible chunks are baked immediately,
 *      the rest are baked lazily in update() at 1–3 chunks per frame.
 */

import { Container, Graphics, Rectangle, type ICanvas } from 'pixi.js';
import { TerrainType, type GridPosition } from '../types';
import type { TileMap } from './TileMap';
import { ISO_TILE_WIDTH, ISO_TILE_HEIGHT, ELEVATION_STEP } from '../constants';
import { isMobileDevice } from '../mapConfig';

const MAX_PULSE_R = 3500;

interface PulseRing {
  t:        number;
  duration: number;
  color:    number;
  origin?:  { x: number; y: number };
  maxRadius?: number;
  alpha?: number;
}

const HW = ISO_TILE_WIDTH  / 2;
const HH = ISO_TILE_HEIGHT / 2;

// Chunk size in tiles per axis. 32×32 → 30 chunks on the 192×144 full map.
const CHUNK_SIZE = 32;
// Chunks baked per frame after the initial eager pass.
const LAZY_PER_FRAME = isMobileDevice() ? 1 : 3;

const PALETTE: Record<TerrainType, number[]> = {
  [TerrainType.Grass]:     [0x4a7a3a, 0x437036, 0x508040, 0x3f6a33],
  [TerrainType.Path]:      [0x8a7858, 0x827052, 0x92805f, 0x847454],
  [TerrainType.Water]:     [0x2f6f9a, 0x357aa6, 0x2f6f9a, 0x357aa6],
  [TerrainType.Corporate]: [0x3d4248, 0x343940, 0x48484a, 0x2e3438],
  [TerrainType.Building]:  [0x7d7a86, 0x787582, 0x82808c, 0x7a7884],
  [TerrainType.Obstacle]:  [0x5a4e3e, 0x524438, 0x625448, 0x4c4034],
  [TerrainType.Locked]:    [0x14161c, 0x14161c, 0x14161c, 0x14161c],
};

const BEACON_TOP      = 0x2f6f6b;
const CORPORATE_HQ_TOP = 0x2b2f3a;
const SE_FACE = 0.7;
const SW_FACE = 0.48;
const OUTLINE = 0x0a0c10;

function shade(color: number, factor: number): number {
  const r = Math.min(255, Math.round(((color >> 16) & 0xff) * factor));
  const g = Math.min(255, Math.round(((color >> 8)  & 0xff) * factor));
  const b = Math.min(255, Math.round( (color & 0xff)        * factor));
  return (r << 16) | (g << 8) | b;
}

export class MapRenderer {
  private readonly layer          = new Container();
  private readonly chunkContainer = new Container();
  private readonly beaconG        = new Graphics();
  private readonly pulseG         = new Graphics();
  private readonly hoverG         = new Graphics();
  private readonly pulseRings: PulseRing[] = [];

  /** Flat array of chunk Graphics; index = chunkRow * chunkCols + chunkCol. */
  private readonly chunkGraphics: (Graphics | null)[] = [];
  /** Queue of [chunkRow, chunkCol] pairs waiting to be baked. */
  private readonly bakePending: Array<[number, number]> = [];
  private chunkCols = 0;
  private chunkRows = 0;

  /** Lazily-computed bounding rect of the full terrain (from corner tiles). */
  private _bounds: Rectangle | null = null;

  constructor(
    private readonly map: TileMap,
    private readonly world: Container,
  ) {}

  init(): void {
    const chunkCols = Math.ceil(this.map.width  / CHUNK_SIZE);
    const chunkRows = Math.ceil(this.map.height / CHUNK_SIZE);
    this.chunkCols = chunkCols;
    this.chunkRows = chunkRows;

    // Allocate Graphics slots for all chunks.
    for (let cr = 0; cr < chunkRows; cr++) {
      for (let cc = 0; cc < chunkCols; cc++) {
        const g = new Graphics();
        g.cullable = true; // Pixi skips draw call when chunk is off-screen
        this.chunkContainer.addChild(g);
        this.chunkGraphics[cr * chunkCols + cc] = g;
      }
    }

    // Sort chunks by distance from map centre so the initial view fills in first.
    const midCr = chunkRows / 2;
    const midCc = chunkCols / 2;
    const all: Array<[number, number]> = [];
    for (let cr = 0; cr < chunkRows; cr++) {
      for (let cc = 0; cc < chunkCols; cc++) all.push([cr, cc]);
    }
    all.sort(([ar, ac], [br, bc]) => {
      const da = (ar - midCr) ** 2 + (ac - midCc) ** 2;
      const db = (br - midCr) ** 2 + (bc - midCc) ** 2;
      return da - db;
    });

    // Bake the centre quarter eagerly so the initial viewport looks complete.
    const eagerCount = Math.ceil(chunkCols * chunkRows * 0.25);
    for (let i = 0; i < all.length; i++) {
      const [cr, cc] = all[i];
      if (i < eagerCount) {
        this.bakeChunk(cc, cr);
      } else {
        this.bakePending.push([cr, cc]);
      }
    }

    this.layer.addChild(this.chunkContainer, this.beaconG, this.pulseG, this.hoverG);
    this.world.addChild(this.layer);
  }

  get bounds(): Rectangle {
    if (!this._bounds) {
      // Compute from the four iso-corner tiles; add generous vertical headroom.
      const corners = [
        this.map.tileTopCenter(0, 0),
        this.map.tileTopCenter(this.map.width - 1, 0),
        this.map.tileTopCenter(0, this.map.height - 1),
        this.map.tileTopCenter(this.map.width - 1, this.map.height - 1),
      ];
      const minX = Math.min(...corners.map(c => c.x)) - HW;
      const maxX = Math.max(...corners.map(c => c.x)) + HW;
      const minY = Math.min(...corners.map(c => c.y)) - HH * 2 - 128;
      const maxY = Math.max(...corners.map(c => c.y)) + HH + 64;
      this._bounds = new Rectangle(minX, minY, maxX - minX, maxY - minY);
    }
    return this._bounds;
  }

  update(delta: number): void {
    // Advance pulse timers.
    for (let i = this.pulseRings.length - 1; i >= 0; i--) {
      this.pulseRings[i].t += delta;
      if (this.pulseRings[i].t >= this.pulseRings[i].duration) {
        this.pulseRings.splice(i, 1);
      }
    }
    // Lazy chunk baking — spread remaining chunks across frames.
    for (let i = 0; i < LAZY_PER_FRAME && this.bakePending.length > 0; i++) {
      const [cr, cc] = this.bakePending.shift()!;
      this.bakeChunk(cc, cr);
    }
  }

  triggerPulse(color = 0x37e0d0, duration = 2.5): void {
    this.pulseRings.push({ t: 0, duration, color });
  }

  triggerPulseAt(tile: GridPosition, color = 0xff2244, duration = 2.5, maxRadius = 700, alpha = 0.42): void {
    this.pulseRings.push({
      t: 0, duration, color,
      origin: this.map.tileTopCenter(tile.col, tile.row),
      maxRadius,
      alpha,
    });
  }

  render(elapsed: number): void {
    const c     = this.map.beaconCenter();
    const pulse = 0.5 + 0.5 * Math.sin(elapsed * 2.4);

    this.beaconG.clear();
    this.beaconG.beginFill(0x37e0d0, 0.1 + 0.18 * pulse);
    this.beaconG.drawCircle(c.x, c.y, HW * 1.6 + pulse * 12);
    this.beaconG.endFill();
    this.beaconG.beginFill(0x6ff0e4, 0.2 + 0.22 * pulse);
    this.beaconG.drawCircle(c.x, c.y, HW * 0.8);
    this.beaconG.endFill();
    this.beaconG.beginFill(0xc9fff8, 0.92);
    this.beaconG.drawCircle(c.x, c.y, 7 + pulse * 2);
    this.beaconG.endFill();

    this.pulseG.clear();
    for (const ring of this.pulseRings) {
      const frac   = ring.t / ring.duration;
      const origin = ring.origin ?? c;
      const r      = frac * (ring.maxRadius ?? MAX_PULSE_R);
      const alpha  = (1 - frac) * (ring.alpha ?? 0.55);
      const lineW  = Math.max(2, 18 * (1 - frac));
      this.pulseG.lineStyle(lineW, ring.color, alpha);
      this.pulseG.drawCircle(origin.x, origin.y, r);
      this.pulseG.lineStyle(Math.max(1, lineW * 0.5), ring.color, alpha * 0.4);
      this.pulseG.drawCircle(origin.x, origin.y, r * 0.92);
    }
  }

  setHover(tile: GridPosition | null): void {
    this.hoverG.clear();
    if (!tile || !this.map.inBounds(tile.col, tile.row)) return;
    const c = this.map.tileTopCenter(tile.col, tile.row);
    this.hoverG.lineStyle(2, 0xffffff, 0.9);
    this.hoverG.drawPolygon([c.x, c.y - HH, c.x + HW, c.y, c.x, c.y + HH, c.x - HW, c.y]);
  }

  // ── Private ─────────────────────────────────────────────────────────────────

  private bakeChunk(chunkCol: number, chunkRow: number): void {
    const g = this.chunkGraphics[chunkRow * this.chunkCols + chunkCol];
    if (!g) return;

    const colStart = chunkCol * CHUNK_SIZE;
    const rowStart = chunkRow * CHUNK_SIZE;
    const colEnd   = Math.min(colStart + CHUNK_SIZE, this.map.width);
    const rowEnd   = Math.min(rowStart + CHUNK_SIZE, this.map.height);

    for (let row = rowStart; row < rowEnd; row++) {
      for (let col = colStart; col < colEnd; col++) {
        this.bakeTile(g, col, row);
      }
    }
  }

  private bakeTile(g: Graphics, col: number, row: number): void {
    const terrain      = this.map.getTerrain({ col, row });
    const variant      = this.map.getVariant(col, row);
    const isBeacon     = this.map.isBeacon(col, row);
    const isCorporateHQ = this.map.isCorporateHQ(col, row);

    const top = isBeacon
      ? BEACON_TOP
      : isCorporateHQ
        ? CORPORATE_HQ_TOP
        : PALETTE[terrain][variant % PALETTE[terrain].length];
    const base       = this.map.tileBase(col, row);
    const faceHeight = this.map.elevation(col, row) * ELEVATION_STEP;
    const cyTop      = base.y - faceHeight;

    if (faceHeight > 0) {
      g.lineStyle(0);
      g.beginFill(shade(top, SE_FACE));
      g.drawPolygon([
        base.x + HW, cyTop,
        base.x,      cyTop + HH,
        base.x,      base.y + HH,
        base.x + HW, base.y,
      ]);
      g.endFill();
      g.beginFill(shade(top, SW_FACE));
      g.drawPolygon([
        base.x - HW, cyTop,
        base.x,      cyTop + HH,
        base.x,      base.y + HH,
        base.x - HW, base.y,
      ]);
      g.endFill();
    }

    g.lineStyle(1, OUTLINE, 0.18);
    g.beginFill(top);
    g.drawPolygon([base.x, cyTop - HH, base.x + HW, cyTop, base.x, cyTop + HH, base.x - HW, cyTop]);
    g.endFill();
  }
}
